import React, { useEffect, useState } from 'react';
import { Box, Sheet, Table, Typography, Chip, IconButton, Tooltip, CircularProgress } from '@mui/joy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { supabase } from '../utils/supabaseClient';
import { PlanetName } from '../utils/astroEngine';
import '../styles/gradientBorder.css';

interface LifeAreaRemediesTableProps {
  /** House scores record (keys 1-12) */
  houseScores: Record<number, number>;
  /** Planet placements (planet name → house number 1-12) */
  placements: Record<string, number>;
  /** House lords (house number → planet name) */
  houseLords: Record<number, PlanetName>;
  /** Planets aspecting each house (house number → planet names array) */
  houseAspects?: Record<number, string[]>;
  /** Planet strengths (optional, for display) */
  planetStrengths?: Partial<Record<PlanetName, number>>;
  /** Function to get planet color */
  getPlanetColor?: (planet: string) => string;
  /** Optional filter to show only specific life area */
  filterLifeArea?: string;
  /** Callback to open Life Area Overview modal */
  onGetOverview?: (lifeAreaName: string) => void;
}

interface LifeArea {
  name: string;
  houses: number[];
  icon: string;
}

const LIFE_AREAS: LifeArea[] = [
  { name: 'Health', houses: [1, 6, 8, 12], icon: '❤️' },
  { name: 'Wealth', houses: [1, 2, 5, 9, 11, 12], icon: '💰' },
  { name: 'Job', houses: [1, 6, 10], icon: '💼' },
  { name: 'Business', houses: [1, 3, 7, 11], icon: '📈' },
  { name: 'Marriage', houses: [1, 7], icon: '💑' },
  { name: 'Children', houses: [1, 5], icon: '👶' }
];

interface Remedy {
  id: string;
  name: string;
  title: string;
  description: string;
  category: string;
  cost_level?: string;
  related_entity: string; // Planet name
  house?: number; // House number from name parsing
}

/**
 * Calculate average house score for a life area
 */
function calculateAvgScore(houses: number[], houseScores: Record<number, number>): number {
  const values = houses.map(h => houseScores[h] ?? 0);
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}

/**
 * Get planets residing in houses for a life area
 */
function getResidingPlanets(
  houses: number[], 
  placements: Record<string, number | { house: number; offsetDeg?: number }>
): { planet: string; house: number }[] {
  const result: { planet: string; house: number }[] = [];
  for (const [planet, placement] of Object.entries(placements)) {
    // Handle both formats: direct number or object with house property
    const house = typeof placement === 'number' ? placement : placement.house;
    if (houses.includes(house)) {
      result.push({ planet, house });
    }
  }
  return result;
}

/**
 * Get aspecting planets for houses in a life area
 */
function getAspectingPlanets(houses: number[], houseAspects?: Record<number, string[]>): { planet: string; house: number }[] {
  if (!houseAspects) return [];
  const result: { planet: string; house: number }[] = [];
  for (const house of houses) {
    const aspecters = houseAspects[house] || [];
    for (const planet of aspecters) {
      result.push({ planet, house });
    }
  }
  return result;
}

export default function LifeAreaRemediesTable(props: LifeAreaRemediesTableProps) {
  const {
    houseScores,
    placements,
    houseLords,
    houseAspects,
    planetStrengths,
    getPlanetColor,
    filterLifeArea,
    onGetOverview
  } = props;

  const [remedies, setRemedies] = useState<Record<string, Remedy[]>>({});
  const [loading, setLoading] = useState(true);
  // Initialize with 'mandatory' as default for all life areas
  const [selectedCategories, setSelectedCategories] = useState<Record<string, string>>(
    LIFE_AREAS.reduce((acc, area) => ({ ...acc, [area.name]: 'mandatory' }), {})
  );

  // Fetch remedies from Supabase (with caching)
  useEffect(() => {
    const fetchRemedies = async () => {
      try {
        // Get all planets from placements and house lords
        const allPlanets = new Set([
          ...Object.keys(placements),
          ...Object.values(houseLords)
        ]);
        
        // Try cache first (permanent - 365 days)
        const cacheKey = `remedies_planets_${Array.from(allPlanets).sort().join('_')}_v1`;
        const cached = localStorage.getItem(cacheKey);
        
        let data;
        if (cached) {
          data = JSON.parse(cached);
        } else {
          // Fetch ALL active planet remedies (includes original 215 + admin-added remedies)
          // Exclude 'other' category which contains interpretations, not remedies
          const { data: fetchedData, error } = await supabase
            .from('remedies')
            .select('id, name, title, description, category, cost_level, related_entity')
            .eq('related_to', 'planet')
            .in('related_entity', Array.from(allPlanets))
            .eq('is_active', true)
            .neq('category', 'other')
            .order('priority', { ascending: false });

          if (error) {
            console.error('[LifeAreaRemediesTable] Error fetching remedies:', error);
            return;
          }
          
          data = fetchedData || [];
          localStorage.setItem(cacheKey, JSON.stringify(data));
        }

        // Group remedies by planet and parse house number from name
        const remedyMap: Record<string, Remedy[]> = {};
        for (const remedy of data || []) {
          const planet = remedy.related_entity;
          // Extract house number from name like "Sun in House 5 - Remedy"
          const houseMatch = remedy.name.match(/House (\d+)/);
          const house = houseMatch ? parseInt(houseMatch[1], 10) : undefined;
          
          if (!remedyMap[planet]) {
            remedyMap[planet] = [];
          }
          remedyMap[planet].push({ ...remedy, house });
        }

        setRemedies(remedyMap);
      } catch (err) {
        console.error('[LifeAreaRemediesTable] Exception:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRemedies();
  }, [placements, houseLords]);

  /**
   * Get remedies for a life area (from all residing planets or lords if empty)
   * Optionally filter by category or 'mandatory' for important remedies only
   */
  function getRemediesForLifeArea(lifeArea: LifeArea, categoryFilter?: string): Remedy[] {
    const residing = getResidingPlanets(lifeArea.houses, placements);
    const aspecting = getAspectingPlanets(lifeArea.houses, houseAspects);
    const remedyMap = new Map<string, Remedy>(); // planet -> single remedy
    
    // ALWAYS get remedies only from residing planets (based on original occupancy)
    if (residing.length > 0) {
      // Sort by strength if available
      const sorted = residing.sort((a, b) => {
        const strA = planetStrengths?.[a.planet as PlanetName] ?? 0;
        const strB = planetStrengths?.[b.planet as PlanetName] ?? 0;
        return strB - strA;
      });
      
      // Get ONE remedy per planet (priority: matching house > no house > skip)
      for (const { planet, house } of sorted) {
        // Skip if we already have a remedy for this planet
        if (remedyMap.has(planet)) continue;
        
        const planetRemedies = remedies[planet];
        if (planetRemedies && planetRemedies.length > 0) {
          // Priority 1: Remedy matching the ACTUAL house where planet resides
          const matchingHouse = planetRemedies.find(r => r.house === house);
          if (matchingHouse) {
            remedyMap.set(planet, matchingHouse);
            continue;
          }
          
          // Priority 2: Generic planet remedy (no house specified)
          const planetSpecific = planetRemedies.find(r => !r.house || r.house === 0);
          if (planetSpecific) {
            remedyMap.set(planet, planetSpecific);
            continue;
          }
          
          // Don't show house-specific remedies for wrong houses
          // Skip this planet if no matching or generic remedy available
        }
      }
    }

    // Apply filter
    if (categoryFilter === 'mandatory') {
      // Do's/Don'ts+Important includes:
      // 1. Lifestyle remedies of ALL occupant planets
      // 2. Important remedies (negative strength) of ALL occupant planets
      // 3. If house is empty: Lifestyle remedies of aspecting planets
      // 4. If house is empty AND no aspects: Lord's Lifestyle remedy
      // EXCLUDES: rudraksha, gemstone, mantra, worship, donation (they have dedicated chips)
      
      const excludedCategories = ['rudraksha', 'gemstone', 'mantra', 'worship', 'donation'];
      const mandatoryPlanets = new Set<string>();
      
      // Process each house in the life area
      for (const house of lifeArea.houses) {
        const residingInHouse = residing.filter(r => r.house === house);
        
        if (residingInHouse.length > 0) {
          // House has occupants - add Lifestyle + Important planets
          for (const { planet } of residingInHouse) {
            const remedy = remedyMap.get(planet);
            if (!remedy) continue;
            
            // Skip excluded categories
            if (remedy.category && excludedCategories.includes(remedy.category.toLowerCase())) {
              continue;
            }
            
            // Include if Lifestyle
            if (remedy.category && remedy.category.toLowerCase() === 'lifestyle') {
              mandatoryPlanets.add(planet);
              continue;
            }
            
            // Include if Important (negative strength)
            const planetStrength = planetStrengths?.[planet as PlanetName];
            if (planetStrength != null && planetStrength < 0) {
              mandatoryPlanets.add(planet);
            }
          }
        } else {
          // House is empty - check for aspecting planets
          const aspectingThisHouse = aspecting.filter(a => a.house === house);
          
          if (aspectingThisHouse.length > 0) {
            // House has aspects - add aspecting planets with remedies
            for (const { planet } of aspectingThisHouse) {
              const planetRemedies = remedies[planet];
              if (!planetRemedies || planetRemedies.length === 0) continue;
              
              // Find where this planet ACTUALLY resides (handle both formats: number or {house: number})
              const placement = placements[planet] as number | { house: number; offsetDeg?: number } | undefined;
              const actualHouse = typeof placement === 'number' ? placement : placement?.house;
              
              mandatoryPlanets.add(planet);
              // Add to remedyMap if not already there (use actual house, not aspected house)
              if (!remedyMap.has(planet)) {
                // Priority 1: Remedy matching ACTUAL house where planet resides
                const matchingHouse = planetRemedies.find(r => r.house === actualHouse);
                if (matchingHouse) {
                  remedyMap.set(planet, matchingHouse);
                } else {
                  // Priority 2: Generic planet remedy (prefer lifestyle, but accept any)
                  const lifestyleRemedy = planetRemedies.find(r => 
                    !r.house && r.category && r.category.toLowerCase() === 'lifestyle'
                  );
                  if (lifestyleRemedy) {
                    remedyMap.set(planet, lifestyleRemedy);
                  } else {
                    // Priority 3: Any generic remedy (no house)
                    const genericRemedy = planetRemedies.find(r => !r.house || r.house === 0);
                    if (genericRemedy) {
                      remedyMap.set(planet, genericRemedy);
                    }
                  }
                }
              }
            }
          } else {
            // House is empty AND no aspects - add Lord
            const lord = houseLords[house];
            if (lord) {
              const lordRemedies = remedies[lord];
              if (lordRemedies) {
                // Find where the lord ACTUALLY resides (handle both formats: number or {house: number})
                const placement = placements[lord] as number | { house: number; offsetDeg?: number } | undefined;
                const actualHouse = typeof placement === 'number' ? placement : placement?.house;
                
                const hasLifestyle = lordRemedies.some(r => 
                  r.category && r.category.toLowerCase() === 'lifestyle'
                );
                if (hasLifestyle) {
                  mandatoryPlanets.add(lord);
                  // Add to remedyMap if not already there (use actual house)
                  if (!remedyMap.has(lord)) {
                    // Priority 1: Remedy matching ACTUAL house where lord resides
                    const matchingHouse = lordRemedies.find(r => r.house === actualHouse);
                    if (matchingHouse) {
                      remedyMap.set(lord, matchingHouse);
                    } else {
                      // Priority 2: Generic planet remedy (no house)
                      const lifestyleRemedy = lordRemedies.find(r => 
                        !r.house && r.category && r.category.toLowerCase() === 'lifestyle'
                      );
                      if (lifestyleRemedy) {
                        remedyMap.set(lord, lifestyleRemedy);
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      
      // Return remedies for mandatory planets only
      return Array.from(mandatoryPlanets).map(p => remedyMap.get(p)!).filter(Boolean);
    } else if (categoryFilter) {
      // Filter by category - return ONE remedy per planet matching the category
      const residing = getResidingPlanets(lifeArea.houses, placements);
      const aspecting = getAspectingPlanets(lifeArea.houses, houseAspects);
      const allPlanets = new Set<string>();
      
      residing.forEach(r => allPlanets.add(r.planet));
      aspecting.forEach(a => allPlanets.add(a.planet));
      
      // If no planets, add house lords
      if (allPlanets.size === 0) {
        lifeArea.houses.forEach(h => {
          const lord = houseLords[h];
          if (lord) allPlanets.add(lord);
        });
      }
      
      // Exception for rudraksha - show ALL rudraksha remedies per planet
      if (categoryFilter === 'rudraksha') {
        const allRudrakshaRemedies: Remedy[] = [];
        for (const planet of allPlanets) {
          const planetRemedies = remedies[planet] || [];
          const rudrakashas = planetRemedies.filter(r => r.category === 'rudraksha');
          allRudrakshaRemedies.push(...rudrakashas);
        }
        return allRudrakshaRemedies;
      }
      
      // Get ONE best remedy per planet matching the category
      const categoryRemedyMap = new Map<string, Remedy>();
      for (const planet of allPlanets) {
        const planetRemedies = remedies[planet] || [];
        // Filter remedies matching this category
        const categoryRemedies = planetRemedies.filter(r => r.category === categoryFilter);
        if (categoryRemedies.length === 0) continue;
        
        // Find where this planet actually resides
        const placement = placements[planet] as number | { house: number; offsetDeg?: number } | undefined;
        const actualHouse = typeof placement === 'number' ? placement : placement?.house;
        
        // Priority: actual house match > generic (no house)
        const matchingHouse = categoryRemedies.find(r => r.house === actualHouse);
        if (matchingHouse) {
          categoryRemedyMap.set(planet, matchingHouse);
        } else {
          // Use generic remedy (no house specified)
          const genericRemedy = categoryRemedies.find(r => !r.house || r.house === 0);
          if (genericRemedy) {
            categoryRemedyMap.set(planet, genericRemedy);
          }
        }
      }
      
      return Array.from(categoryRemedyMap.values());
    }

    // No filter - return all remedies (one per planet)
    return Array.from(remedyMap.values());
  }

  /**
   * Get unique categories from remedies for a life area with counts
   * Counts unique remedy IDs per category (avoids duplicate counts)
   */
  function getCategoriesForLifeArea(lifeArea: LifeArea): { category: string; count: number }[] {
    const categoryCounts = new Map<string, Set<string>>();
    
    // Get all relevant planets for this life area
    const residing = getResidingPlanets(lifeArea.houses, placements);
    const aspecting = getAspectingPlanets(lifeArea.houses, houseAspects);
    const allPlanets = new Set<string>();
    
    residing.forEach(r => allPlanets.add(r.planet));
    aspecting.forEach(a => allPlanets.add(a.planet));
    
    // If no planets, add house lords
    if (allPlanets.size === 0) {
      lifeArea.houses.forEach(h => {
        const lord = houseLords[h];
        if (lord) allPlanets.add(lord);
      });
    }
    
    // Count unique PLANETS per category (one remedy per planet logic)
    // Exception: rudraksha shows ALL remedies per planet
    for (const planet of allPlanets) {
      const planetRemedies = remedies[planet] || [];
      for (const remedy of planetRemedies) {
        if (remedy.category) {
          if (!categoryCounts.has(remedy.category)) {
            categoryCounts.set(remedy.category, new Set());
          }
          // Exception for rudraksha: count each remedy individually
          if (remedy.category === 'rudraksha') {
            const uniqueKey = remedy.id || remedy.name || remedy.title;
            categoryCounts.get(remedy.category)!.add(uniqueKey);
          } else {
            // Track planet (not remedy ID) - one planet counted once per category
            categoryCounts.get(remedy.category)!.add(planet);
          }
        }
      }
    }
    
    return Array.from(categoryCounts.entries())
      .map(([category, planetSet]) => ({ category, count: planetSet.size }))
      .sort((a, b) => b.count - a.count); // Sort by count descending
  }

  /**
   * Get count of mandatory/important remedies for a life area
   */
  function getMandatoryCount(lifeArea: LifeArea): number {
    return getRemediesForLifeArea(lifeArea, 'mandatory').length;
  }

  /**
   * Format score with sign and color
   */
  function formatScore(score: number): { text: string; color: string } {
    const sign = score > 0 ? '+' : score < 0 ? '' : '';
    const color = score > 8 ? '#16a34a' : score > 0 ? '#84cc16' : score > -8 ? '#f97316' : '#dc2626';
    return { text: `${sign}${score.toFixed(1)}`, color };
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  const filteredAreas = LIFE_AREAS.filter(area => !filterLifeArea || filterLifeArea === 'ALL' || area.name === filterLifeArea);

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography level="title-lg" sx={{ fontWeight: 600, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
          Life Areas Summary with Remedies
        </Typography>
        <Tooltip
          title="Shows each major life area with its house numbers, average strength score, influencing planets (residing/aspecting/lord) with their scores, and recommended remedy based on the strongest planet or house lord."
          placement="top"
          arrow
          variant="soft"
          size="sm"
          sx={{ maxWidth: 320, p: 1, fontSize: '0.875rem' }}
        >
          <IconButton size="sm" variant="plain" color="primary" sx={{ minWidth: 18, minHeight: 18, padding: '2px' }}>
            <HelpOutlineIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Mobile Card View (xs only) */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 2 }}>
        {filteredAreas.map((area) => {
          const avgScore = calculateAvgScore(area.houses, houseScores);
          const residing = getResidingPlanets(area.houses, placements);
          const aspecting = getAspectingPlanets(area.houses, houseAspects);
          const areaRemedies = getRemediesForLifeArea(area, selectedCategories[area.name]);
          const scoreFormatted = formatScore(avgScore);
          const lords = area.houses.map(h => ({ house: h, lord: houseLords[h] }));

          return (
            <Sheet key={area.name} variant="outlined" sx={{ p: 2, borderRadius: 'md' }} data-life-area={area.name}>
              {/* Life Area Header with Score */}
              <Box sx={{ mb: 1.5 }}>
                <Typography level="title-md" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {area.icon} {area.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                    Houses: {area.houses.join(', ')}
                  </Typography>
                  <Chip
                    size="sm"
                    sx={{
                      fontWeight: 700,
                      bgcolor: `${scoreFormatted.color}20`,
                      color: scoreFormatted.color,
                      border: `1px solid ${scoreFormatted.color}`,
                      fontSize: '0.75rem'
                    }}
                  >
                    Avg: {scoreFormatted.text}
                  </Chip>
                </Box>
              </Box>

              {/* Influencing Planets */}
              <Box sx={{ mb: 1.5 }}>
                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Influencing Planets
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {residing.length > 0 && (
                    <Box>
                      <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.7 }}>
                        Residing:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {residing.map(({ planet, house }) => {
                          const strength = planetStrengths?.[planet as PlanetName];
                          const color = getPlanetColor ? getPlanetColor(planet) : '#666';
                          return (
                            <Chip
                              key={`${planet}-${house}`}
                              size="sm"
                              variant="soft"
                              sx={{
                                fontSize: '0.7rem',
                                bgcolor: `${color}20`,
                                color: color,
                                border: `1px solid ${color}50`
                              }}
                            >
                              {planet} (H{house}) {strength != null ? `${strength > 0 ? '+' : ''}${strength.toFixed(0)}` : ''}
                            </Chip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {aspecting.length > 0 && (
                    <Box>
                      <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.7 }}>
                        Aspecting:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {aspecting.slice(0, 10).map(({ planet, house }, idx) => {
                          const strength = planetStrengths?.[planet as PlanetName];
                          const color = getPlanetColor ? getPlanetColor(planet) : '#666';
                          return (
                            <Chip
                              key={`${planet}-${house}-${idx}`}
                              size="sm"
                              variant="outlined"
                              sx={{
                                fontSize: '0.65rem',
                                borderColor: `${color}50`,
                                color: color
                              }}
                            >
                              {planet}→H{house} {strength != null ? `${strength > 0 ? '+' : ''}${strength.toFixed(0)}` : ''}
                            </Chip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  <Box>
                    <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25, fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.7 }}>
                      Lords:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {lords.map(({ house, lord }) => {
                        const strength = planetStrengths?.[lord];
                        const color = getPlanetColor ? getPlanetColor(lord) : '#666';
                        return (
                          <Chip
                            key={`lord-${house}`}
                            size="sm"
                            variant="plain"
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: 'background.level2',
                              color: color,
                              fontWeight: 500
                            }}
                          >
                            H{house}: {lord} {strength != null ? `(${strength > 0 ? '+' : ''}${strength.toFixed(0)})` : ''}
                          </Chip>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Category Filter Chips */}
              <Box sx={{ mb: 1.5 }}>
                <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.75 }}>
                  Filter Remedies
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    size="sm"
                    variant={selectedCategories[area.name] === 'mandatory' ? 'solid' : 'outlined'}
                    color={selectedCategories[area.name] === 'mandatory' ? 'danger' : 'neutral'}
                    onClick={() => {
                      setSelectedCategories(prev => ({
                        ...prev,
                        [area.name]: 'mandatory'
                      }));
                    }}
                    sx={{ cursor: 'pointer', fontSize: '0.65rem', fontWeight: 600, py: 0.25, touchAction: 'manipulation', userSelect: 'none' }}
                  >
                    Do's/Don'ts + Important ({getMandatoryCount(area)})
                  </Chip>
                  {getCategoriesForLifeArea(area).map(({ category, count }) => (
                    <Chip
                      key={category}
                      size="sm"
                      variant={selectedCategories[area.name] === category ? 'solid' : 'outlined'}
                      color={selectedCategories[area.name] === category ? 'primary' : 'neutral'}
                      onClick={() => {
                        setSelectedCategories(prev => ({
                          ...prev,
                          [area.name]: category
                        }));
                      }}
                      sx={{ cursor: 'pointer', fontSize: '0.65rem', py: 0.25, touchAction: 'manipulation', userSelect: 'none' }}
                    >
                      {category} ({count})
                    </Chip>
                  ))}
                </Box>
              </Box>

              {/* Remedies */}
              {areaRemedies.length > 0 && (
                <Box>
                  <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.75 }}>
                    {selectedCategories[area.name] ? `${selectedCategories[area.name]} Remedies` : 'Recommended Remedies'}
                  </Typography>
                  {selectedCategories[area.name] === 'donation' && (
                    <Box sx={{ mb: 1, p: 1, bgcolor: 'warning.softBg', borderRadius: 'sm', border: '1px solid', borderColor: 'warning.outlinedBorder' }}>
                      <Typography level="body-xs" sx={{ color: 'warning.700', fontWeight: 600, fontSize: '0.7rem' }}>
                        ⚠️ Important: Donation remedies only for the planets in red and -ve score
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {areaRemedies.map((remedy, idx) => {
                      const isImportant = (() => {
                        const planet = remedy.related_entity;
                        const planetStrength = planetStrengths?.[planet as PlanetName];
                        // Mark as important ONLY if this planet itself has negative strength
                        return planetStrength != null && planetStrength < 0;
                      })();

                      return (
                        <Box key={remedy.id || idx} sx={{ 
                          pb: idx < areaRemedies.length - 1 ? 1.5 : 0,
                          borderBottom: idx < areaRemedies.length - 1 ? '1px solid' : 'none',
                          borderColor: 'divider'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                            <Typography level="body-sm" sx={{ fontWeight: 600, flex: 1, fontSize: '0.875rem' }}>
                              {remedy.title}
                            </Typography>
                            {isImportant && (
                              <Chip size="sm" color="danger" variant="soft" sx={{ fontSize: '0.65rem', py: 0, px: 0.75, height: 'auto' }}>
                                Important
                              </Chip>
                            )}
                          </Box>
                          <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.4, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {(() => {
                              const text = remedy.description || '';
                              
                              // First try to extract "Remedies:" section
                              const remediesMatch = text.match(/Remedies?:\s*(.*?)(?:\n\n|$)/is);
                              if (remediesMatch) return remediesMatch[1].trim();
                              
                              // If text has bullet points, preserve all
                              if (text.includes('--') || text.includes('•') || text.includes('*')) {
                                const lines = text.split(/(?:--|•|\*)\s*/).filter(l => l.trim()).map(l => l.trim());
                                if (lines.length > 1) {
                                  return lines.map(line => `• ${line}`).join('\n');
                                }
                              }
                              
                              // Fallback: show full text
                              return text.length > 300 ? text.substring(0, 300) + '...' : text;
                            })()}
                          </Typography>
                          {remedy.category && (
                            <Chip size="sm" variant="outlined" sx={{ mt: 0.5, fontSize: '0.65rem', py: 0, px: 0.75, height: 'auto' }}>
                              {remedy.category}
                            </Chip>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Sheet>
          );
        })}
      </Box>

      {/* Desktop Table View (sm+) */}
      <Sheet variant="outlined" sx={{ display: { xs: 'none', sm: 'block' }, borderRadius: 'md', overflow: 'auto' }}>
        <Table
          stickyHeader
          sx={{
            '& th': {
              bgcolor: 'background.level1',
              fontWeight: 600,
              fontSize: '0.875rem'
            },
            '& td': {
              fontSize: '0.875rem',
              py: 1.5,
              verticalAlign: 'top'
            }
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '15%' }}>Life Area (Houses)</th>
              <th style={{ width: '8%', textAlign: 'center' }}>Avg Score</th>
              <th style={{ width: '30%' }}>Influencing Planets</th>
              <th style={{ width: '47%' }}>Recommended Remedy</th>
            </tr>
          </thead>
          <tbody>
            {filteredAreas.map((area) => {
              const avgScore = calculateAvgScore(area.houses, houseScores);
              const residing = getResidingPlanets(area.houses, placements);
              const aspecting = getAspectingPlanets(area.houses, houseAspects);
              const categories = getCategoriesForLifeArea(area);
              const selectedCategory = selectedCategories[area.name];
              const areaRemedies = getRemediesForLifeArea(area, selectedCategory);
              const scoreFormatted = formatScore(avgScore);

              // Get lords for all houses in this life area
              const lords = area.houses.map(h => ({
                house: h,
                lord: houseLords[h]
              }));

              return (
                <tr key={area.name} data-life-area={area.name}>
                  {/* Column 1: Life Area + Houses */}
                  <td>
                    <Box>
                      <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {area.icon} {area.name}
                      </Typography>
                      <Typography level="body-xs" sx={{ opacity: 0.7, mb: 0.75 }}>
                        Houses: {area.houses.join(', ')}
                      </Typography>
                      {onGetOverview && (
                        <button
                          onClick={() => onGetOverview(area.name)}
                          className="gradient-border-button"
                          style={{
                            flexShrink: 0
                          }}
                        >
                          <div className="gradient-border-button-inner" style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                            <span>📊 Life Area Overview</span>
                          </div>
                        </button>
                      )}
                    </Box>
                  </td>

                  {/* Column 2: Average Score */}
                  <td style={{ textAlign: 'center' }}>
                    <Chip
                      size="sm"
                      sx={{
                        fontWeight: 700,
                        bgcolor: `${scoreFormatted.color}20`,
                        color: scoreFormatted.color,
                        border: `1px solid ${scoreFormatted.color}`
                      }}
                    >
                      {scoreFormatted.text}
                    </Chip>
                  </td>

                  {/* Column 3: Planets + Scores */}
                  <td>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {/* Residing planets */}
                      {residing.length > 0 && (
                        <Box>
                          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25 }}>
                            Residing:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {residing.map(({ planet, house }) => {
                              const strength = planetStrengths?.[planet as PlanetName];
                              const color = getPlanetColor ? getPlanetColor(planet) : '#666';
                              return (
                                <Chip
                                  key={`${planet}-${house}`}
                                  size="sm"
                                  variant="soft"
                                  sx={{
                                    fontSize: '0.75rem',
                                    bgcolor: `${color}20`,
                                    color: color,
                                    border: `1px solid ${color}50`
                                  }}
                                >
                                  {planet} (H{house}) {strength != null ? `${strength > 0 ? '+' : ''}${strength.toFixed(0)}` : ''}
                                </Chip>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* Aspecting planets */}
                      {aspecting.length > 0 && (
                        <Box>
                          <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25 }}>
                            Aspecting:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {aspecting.slice(0, 10).map(({ planet, house }, idx) => {
                              const strength = planetStrengths?.[planet as PlanetName];
                              const color = getPlanetColor ? getPlanetColor(planet) : '#666';
                              return (
                                <Chip
                                  key={`${planet}-${house}-${idx}`}
                                  size="sm"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    borderColor: `${color}50`,
                                    color: color
                                  }}
                                >
                                  {planet}→H{house} {strength != null ? `${strength > 0 ? '+' : ''}${strength.toFixed(0)}` : ''}
                                </Chip>
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {/* House Lords */}
                      <Box>
                        <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.25 }}>
                          Lords:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {lords.map(({ house, lord }) => {
                            const strength = planetStrengths?.[lord];
                            const color = getPlanetColor ? getPlanetColor(lord) : '#666';
                            return (
                              <Chip
                                key={`lord-${house}`}
                                size="sm"
                                variant="plain"
                                sx={{
                                  fontSize: '0.75rem',
                                  bgcolor: 'background.level2',
                                  color: color,
                                  fontWeight: 500
                                }}
                              >
                                H{house}: {lord} {strength != null ? `(${strength > 0 ? '+' : ''}${strength.toFixed(0)})` : ''}
                              </Chip>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  </td>

                  {/* Column 4: Remedies (all residing planets) */}
                  <td>
                    {/* Category filter chips */}
                    {categories.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                        <Chip
                          size="sm"
                          variant={selectedCategory === 'mandatory' ? 'solid' : 'outlined'}
                          color={selectedCategory === 'mandatory' ? 'danger' : 'neutral'}
                          onClick={() => {
                            setSelectedCategories(prev => ({
                              ...prev,
                              [area.name]: 'mandatory'
                            }));
                          }}
                          sx={{ cursor: 'pointer', fontSize: '0.7rem', fontWeight: 600 }}
                        >
                          Do's/Don'ts + Important ({getMandatoryCount(area)})
                        </Chip>
                        {categories.map(({ category, count }) => (
                          <Chip
                            key={category}
                            size="sm"
                            variant={selectedCategory === category ? 'solid' : 'outlined'}
                            color={selectedCategory === category ? 'primary' : 'neutral'}
                            onClick={() => {
                              setSelectedCategories(prev => ({
                                ...prev,
                                [area.name]: category
                              }));
                            }}
                            sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                          >
                            {category} ({count})
                          </Chip>
                        ))}
                      </Box>
                    )}
                    
                    {selectedCategory === 'donation' && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: 'warning.softBg', borderRadius: 'sm', border: '1px solid', borderColor: 'warning.outlinedBorder' }}>
                        <Typography level="body-xs" sx={{ color: 'warning.700', fontWeight: 600, fontSize: '0.7rem' }}>
                          ⚠️ Important: Donation remedies only for the planets in red and -ve score
                        </Typography>
                      </Box>
                    )}
                    
                    {areaRemedies.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {areaRemedies.map((remedy, idx) => {
                          // Check if this remedy is for a planet with negative strength
                          const isImportant = (() => {
                            const planet = remedy.related_entity;
                            const planetStrength = planetStrengths?.[planet as PlanetName];
                            // Mark as important ONLY if this planet itself has negative strength
                            return planetStrength != null && planetStrength < 0;
                          })();

                          return (
                            <Box key={remedy.id || idx} sx={{ 
                              pb: idx < areaRemedies.length - 1 ? 1.5 : 0,
                              borderBottom: idx < areaRemedies.length - 1 ? '1px solid' : 'none',
                              borderColor: 'divider'
                            }}>
                              <Typography level="body-sm" sx={{ fontWeight: 600, mb: 0.25 }}>
                                {remedy.title}
                              </Typography>
                              <Typography level="body-xs" sx={{ mb: 0.75, color: 'text.secondary', lineHeight: 1.4 }}>
                                {(() => {
                                  const text = remedy.description || '';
                                  
                                  // First try to extract "Remedies:" section
                                  const remediesMatch = text.match(/Remedies?:\s*(.*?)(?:\n\n|$)/is);
                                  if (remediesMatch) return remediesMatch[1].trim();
                                  
                                  // If text has bullet points (-- or • or *), preserve all of them
                                  if (text.includes('--') || text.includes('•') || text.includes('*')) {
                                    // Split by bullet markers and rejoin with consistent formatting
                                    const lines = text.split(/(?:--|•|\*)\s*/).filter(l => l.trim()).map(l => l.trim());
                                    if (lines.length > 1) {
                                      return lines.map(line => `• ${line}`).join('\n');
                                    }
                                  }
                                  
                                  // Fallback: show full text (or truncate if too long)
                                  return text.length > 300 ? text.substring(0, 300) + '...' : text;
                                })()}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip size="sm" variant="soft" color="primary">
                                  {remedy.category}
                                </Chip>
                                {remedy.cost_level && (
                                  <Chip size="sm" variant="outlined" color="neutral">
                                    {remedy.cost_level}
                                  </Chip>
                                )}
                                {isImportant && (
                                  <Chip size="sm" variant="solid" color="danger" sx={{ fontWeight: 700 }}>
                                    Important
                                  </Chip>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography level="body-xs" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
                        No remedy available
                      </Typography>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>
    </Box>
  );
}
